/**
 * useProviderSummary Hook
 *
 * Fetches detailed statistics for a single provider
 * Supports both provider and admin endpoints via organizationId parameter
 */

import { skipToken } from '@reduxjs/toolkit/query';

import { useGetAdminProviderSummaryQuery } from '../store/adminApi';
import { useGetProviderSummaryQuery } from '../store/api';
import type { ProviderSummary } from '../types';

interface UseProviderSummaryParams {
  /** Provider user ID */
  userId: string;
  /** Organization ID for admin mode (if provided, uses admin endpoint) */
  organizationId?: string;
  /** Period in days */
  days?: number;
  /** Explicit start date (ISO 8601 format) */
  dateFrom?: string;
  /** Explicit end date (ISO 8601 format) */
  dateTo?: string;
}

interface UseProviderSummaryResult {
  /** Provider summary data */
  data: ProviderSummary | undefined;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
}

export const useProviderSummary = (
  params: UseProviderSummaryParams,
): UseProviderSummaryResult => {
  const { userId, organizationId, days, dateFrom, dateTo } = params;

  const filterParams = {
    ...(days !== undefined && { days }),
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo }),
  };

  // Use admin endpoint if organizationId provided, otherwise use provider endpoint
  const adminQuery = useGetAdminProviderSummaryQuery(
    organizationId && userId
      ? {
          organizationId,
          userId,
          ...filterParams,
        }
      : skipToken,
  );

  const providerQuery = useGetProviderSummaryQuery(
    !organizationId && userId
      ? {
          userId,
          ...filterParams,
        }
      : skipToken,
  );

  // Use whichever query is active
  const { data, isLoading, error, refetch } = organizationId ? adminQuery : providerQuery;

  return {
    data: data as ProviderSummary | undefined,
    isLoading,
    error: error ? 'Failed to load provider summary' : null,
    refetch,
  };
};

export default useProviderSummary;

